// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

// Polyfill for crypto.randomUUID (Safari < 15.4 compatibility)
if (typeof crypto !== 'undefined' && !crypto.randomUUID) {
  crypto.randomUUID = function (): `${string}-${string}-${string}-${string}-${string}` {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // Version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // Variant 1
    const hex = [...bytes].map(b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}` as `${string}-${string}-${string}-${string}-${string}`;
  };
}

import { StrictMode, Fragment, type ReactNode } from 'react';
import { library, config } from '@fortawesome/fontawesome-svg-core';
import {
  faBrain,
  faQuestion,
  faBinoculars,
  faBomb,
  faCamera,
  faCompass,
  faFeather,
  faHammer,
  faMagnet,
  faPaperclip,
  faRocket,
  faScroll,
  faTrophy,
  faUmbrella,
  faWandMagicSparkles,
  faWineGlass,
  faSyringe,
  faPills,
  faStethoscope,
  faHeartPulse,
  faDna,
  faMicroscope,
  faVial,
  faBandage,
  faHospital,
  faFlask,
  faCouch,
  faBook,
  faClock,
  faKey,
  faLightbulb,
  faPuzzlePiece,
  faBell,
  faGem,
  faCrown,
  faMoon,
  faSun,
  faCloud,
  faTree,
  faStar,
  faLeaf,
  faSnowflake,
  faFire,
  faWater,
  faMountain,
  faBolt,
  faGhost,
  faEye,
  faHand,
  faShield,
  faInfinity,
  faYinYang,
  faAtom,
  faCircleNotch,
  faRotate,
  faDove,
  faDragon,
  faSpider,
  faFish,
  faCat,
  faDog,
  faCrow,
  faFrog,
  faHorse,
  faBug,
  faAnchor,
} from '@fortawesome/free-solid-svg-icons';
import { createRoot } from 'react-dom/client';

// Self-hosted fonts via fontsource
import '@fontsource/righteous/index.css';
import '@fontsource/nunito/400.css';
import '@fontsource/nunito/500.css';
import '@fontsource/nunito/600.css';
import '@fontsource/nunito/700.css';
import '@fontsource/nunito/800.css';
import '@fontsource/fira-code/500.css';
import '@fontsource/fira-code/700.css';

import './index.css';
import App from './App.tsx';

// FontAwesome configuration - prevent FOUT (Flash of Unstyled Text)
import '@fortawesome/fontawesome-svg-core/styles.css';
// Import only the specific icons used in the app (tree-shaking)

// PWA Service Worker
import { logger } from './lib/logger';
import { offlineStorage } from './lib/offline';
import { registerServiceWorker } from './lib/serviceWorker';

// Disable automatic CSS insertion to prevent flashing
config.autoAddCss = false;

// Add only the specific icons used in the app
library.add(
  faBrain,
  faQuestion,
  faBinoculars,
  faBomb,
  faCamera,
  faCompass,
  faFeather,
  faHammer,
  faMagnet,
  faPaperclip,
  faRocket,
  faScroll,
  faTrophy,
  faUmbrella,
  faWandMagicSparkles,
  faWineGlass,
  faSyringe,
  faPills,
  faStethoscope,
  faHeartPulse,
  faDna,
  faMicroscope,
  faVial,
  faBandage,
  faHospital,
  faFlask,
  faCouch,
  faBook,
  faClock,
  faKey,
  faLightbulb,
  faPuzzlePiece,
  faBell,
  faGem,
  faCrown,
  faMoon,
  faSun,
  faCloud,
  faTree,
  faStar,
  faLeaf,
  faSnowflake,
  faFire,
  faWater,
  faMountain,
  faBolt,
  faGhost,
  faEye,
  faHand,
  faShield,
  faInfinity,
  faYinYang,
  faAtom,
  faCircleNotch,
  faRotate,
  faDove,
  faDragon,
  faSpider,
  faFish,
  faCat,
  faDog,
  faCrow,
  faFrog,
  faHorse,
  faBug,
  faAnchor
);

// Global error handler for uncaught errors (non-React errors)
window.onerror = (message, source, lineno, colno, error) => {
  logger.error('global_uncaught_error', {
    message: String(message),
    source,
    lineno,
    colno,
    stack: error?.stack,
  });
  return false; // Let default handling continue
};

// Global handler for unhandled promise rejections
window.onunhandledrejection = (event: PromiseRejectionEvent) => {
  logger.error('unhandled_promise_rejection', {
    reason: event.reason?.message || String(event.reason),
    stack: event.reason?.stack,
  });
};

// Register service worker for PWA functionality
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    registerServiceWorker({
      onSuccess: registration => {
        logger.info('PWA ready for offline use', { scope: registration.scope });
        // Initialize offline storage
        offlineStorage.init().catch(error => {
          logger.error('Failed to initialize offline storage', { error });
        });
      },
      onUpdate: registration => {
        logger.info('New version available', { scope: registration.scope });
        // Could show a notification to user here
      },
      onOffline: () => {
        logger.info('App went offline');
      },
      onOnline: () => {
        logger.info('App came back online');
      },
    }).catch(error => {
      logger.error('Service worker registration error', { error });
    });
  });
}

// Conditionally disable StrictMode in development to prevent WebSocket
// double-mounting issues. StrictMode intentionally mounts components twice
// in dev to detect side effects, which causes WebSocket connections to
// connect → disconnect → reconnect in a rapid loop.
// In production, this wrapper is equivalent to StrictMode.
// Export to fix Fast Refresh requirement (components must be exported)
export const StrictModeWrapper = ({ children }: { children: ReactNode }) => {
  // In development: use Fragment (no double-mounting)
  // In production: use StrictMode (full React checks)
  const Wrapper = import.meta.env.DEV ? Fragment : StrictMode;
  return <Wrapper>{children}</Wrapper>;
};

createRoot(document.getElementById('root')!).render(
  <StrictModeWrapper>
    <App />
  </StrictModeWrapper>
);
